// CreditNova Workflow Navigation Component
// Provides prev/next navigation for workflow steps
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Save } from 'lucide-react';
import { useWorkflow } from '@/contexts/WorkflowContext';

// ===========================================
// Types
// ===========================================

interface WorkflowNavigationProps {
  onNext?: () => void | Promise<void>;
  onPrevious?: () => void;
  onSave?: () => void | Promise<void>;
  nextLabel?: string;
  previousLabel?: string;
  showSave?: boolean;
  saveLabel?: string;
  isNextDisabled?: boolean;
  isLoading?: boolean;
}

// ===========================================
// WorkflowNavigation Component
// ===========================================

export function WorkflowNavigation({
  onNext,
  onPrevious,
  onSave,
  nextLabel,
  previousLabel,
  showSave = false,
  saveLabel = 'Save Progress',
  isNextDisabled = false,
  isLoading = false,
}: WorkflowNavigationProps) {
  const { nextStep, previousStep, canGoNext, canGoPrevious, availableSteps, stepIndex } = useWorkflow();

  const isLastStep = stepIndex === availableSteps.length - 1;
  const isFirstStep = stepIndex === 0;

  const handleNext = async () => {
    // Force-commit any active input (e.g. an override field the user is
    // still focused on) by blurring it before we navigate.  The blur
    // triggers the field's onBlur → handleCommit → onEdit chain, ensuring
    // the value shown on screen is the value that gets saved.
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    // Yield a tick so React can process the state updates from the blur
    await new Promise((r) => setTimeout(r, 0));

    if (onNext) {
      await onNext();
    }
    if (!isLastStep) {
      nextStep();
    }
  };

  const handlePrevious = async () => {
    // Same blur-commit safety as handleNext
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    await new Promise((r) => setTimeout(r, 0));

    if (onPrevious) {
      onPrevious();
    }
    if (!isFirstStep) {
      previousStep();
    }
  };

  return (
    <div className="flex items-center justify-between py-6 px-8 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div>
        {canGoPrevious && (
          <Button variant="outline" onClick={handlePrevious} size="lg" disabled={isLoading}>
            <ChevronLeft className="w-5 h-5 mr-2" />
            {previousLabel || 'Previous'}
          </Button>
        )}
      </div>

      <div className="flex items-center gap-4">
        {showSave && onSave && (
          <Button variant="outline" onClick={onSave} disabled={isLoading}>
            <Save className="w-4 h-4 mr-2" />
            {saveLabel}
          </Button>
        )}

        {canGoNext && (
          <Button
            onClick={handleNext}
            size="lg"
            disabled={isNextDisabled || isLoading}
          >
            {isLoading ? (
              <span className="flex items-center">
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                Processing...
              </span>
            ) : (
              <>
                {nextLabel || (isLastStep ? 'Generate Report' : 'Continue')}
                <ChevronRight className="w-5 h-5 ml-2" />
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}

